import { ComponentType, ReactElement, ReactNode } from 'react';
import { PageTheme } from '@backstage/theme';
import { Entity } from '@backstage/catalog-model';
import { Notification } from '@backstage/plugin-notifications-common';
import { LinkLibraryLink } from '@internal/backstage-plugin-hm-common';

export type CardLink = {
  name: string;
  link: string;
  icon: ComponentType;
  description: string;
  tags: string[]; // Note: changed to string[] from LinkTags[]
};

export type CardLinks = {
  label: string;
  href: string;
}[];

export type EntitySpec = {
  type?: string;
  owner?: string;
};

export enum CardVariant {
  minimal = 'minimal',
  normal = 'normal',
}

export enum CardType {
  dashboard = 'dashboard',
  documentation = 'documentation',
  home = 'home',
  onboarding = 'onboarding',
  playground = 'playground',
  tool = 'tool',
  library = 'library',
  other = 'other',
  plugin = 'plugin',
  website = 'website',
  service = 'service',
  apis = 'apis',
  openapi = 'openapi',
  cache = 'cache',
  database = 'database',
  'kubernetes-cluster' = 'kubernetes-cluster',
  storage = 'storage',
  announcement = 'announcement',
  notification = 'notification',
  link = 'link',
}

type CardStyles = Partial<
  Record<
    | 'card'
    | 'avatar'
    | 'header'
    | 'title'
    | 'subheader'
    | 'icon'
    | 'content'
    | 'actions',
    Record<string, string | number>
  >
>;

export type CardProps = {
  entity?: Entity;
  link?: LinkLibraryLink;
  Icon?: ComponentType;
  avatar?: ReactElement;
  title?: string;
  maxTitleLength?: number;
  type?: CardType | Notification['payload']['severity'];
  description?: string;
  maxDescriptionLength?: number;
  actionRoute?: string;
  actionLabel?: string;
  links?: CardLinks;
  target?: '_self' | '_blank';
  children?: ReactNode;
  styles?: CardStyles;
  variant?: CardVariant;
  created?: string;
  actionable?: boolean;
  withoutTheme?: boolean;
  withoutTags?: boolean;
  withoutLinks?: boolean;
  fullHeight?: boolean;
};

export type NotificationCardProps = Omit<CardProps, 'entity' | 'type'> & {
  notification: Notification;
};

type Announcement = {
  id: number;
  title: string;
  created: string;
  tags: string[];
};

export type AnnouncementCardProps = Omit<CardProps, 'entity' | 'type'> & {
  announcement: Announcement;
};

export type DashboardCardProps = Omit<CardProps, 'entity'> & {
  media?: string;
  owner?: string;
};

export type TemplateCardProps = Omit<CardProps, 'type'>;

export type TechdocsCardProps = Omit<CardProps, 'type'>;

export type EntityCardProps = Omit<CardProps, 'type'> & {
  entity: Entity;
};

export type LinkCardProps = Omit<
  CardProps,
  'type' | 'link' | 'actionRoute' | 'actionLabel'
> & {
  link: LinkLibraryLink;
};

export type CardContextValue = {
  entity?: Entity;
  link?: LinkLibraryLink;
  Icon?: ComponentType;
  avatar?: ReactElement;
  title?: string;
  maxTitleLength?: number;
  type?: string;
  description?: string;
  maxDescriptionLength?: number;
  catalogRoute?: string;
  actionRoute?: string;
  actionLabel?: string;
  themedStyles: Record<string, string>;
  externalStyles?: CardStyles;
  themeForType: PageTheme;
  variant: CardVariant;
  actionable?: boolean;
  withoutTheme?: boolean;
  withoutTags?: boolean;
  withoutLinks?: boolean;
  fullHeight?: boolean;
  created?: string;
};
